import React, { useEffect, useState, useRef, Fragment } from "react"
import { Link, useParams, useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import Select, { components } from 'react-select'
import MaskedInput from 'react-text-mask'
import { Modal } from 'react-bootstrap'
import { SketchPicker } from "@hello-pangea/color-picker";

import Size from './Size'
import Modifiers from './Modifier'
import { notifyProductsChanged } from './productsSync'
import ReactSelect from 'pages/parts/ReactSelect'

import { GET, POST, PUT, FILE, DELETE, httpOk } from 'helpers/api'
import { findKeyFromArrayByValue, formatDate, formatMoney, notify } from "helpers/helpers"
import { smiles } from "helpers/smiles"
import DeleteModal from "pages/parts/DeleteModal"
import QuickElementModal from 'pages/main/elements/QuickElementModal'
import HintIcon from 'pages/parts/HintIcon'
import { ProductLabelBadge } from 'pages/main/productLabels/Index'

// Столько меток помещается на карточке блюда у гостя (сайт, бот, приложение)
const PRODUCT_LABELS_MAX = 2

// «kg» элемента в этих единицах — выход после чистки (помидор 0.85), а не вес штуки:
// брутто = кол-во, выход идёт в холодные потери. Тот же список — IngredientWeightService на бэке
const WEIGHT_UNIT_NAMES = ['кг', 'литр', 'гр', 'мл']

function isWeightUnitElement(item) {
	return WEIGHT_UNIT_NAMES.includes(item?.unit_name)
}

function CreateUpdate({
	productId = null,
	productPosId = null,
	embedded = false,
	onClose = null,
	initialMainTab = 1,
}) {
	const { t } = useTranslation()
	const routeParams = useParams()
	const location = useLocation()
	const posId = productPosId ?? routeParams.posId
	const id = productId ?? routeParams.id
	const navigate = useNavigate()

	const inputRef = useRef(null);
	const tbodyRef = useRef()
	const scrollRef = useRef()

	const writeOffTypes = [
		{ 'id': 1, 'name': t('ingredients') },
		{ 'id': 2, 'name': t('product') },
	]

	const [data, setData] = useState({
		'name': '',
		'menu_id': null,
		'product_type_id': 1,
		'unit_id': 1,
		'status': true,
		'quantity': 1,
		'sale_price': "",
		'cost_price': 0,
		'write_off_type_id': 1,
		'is_divisible': true,
		'is_custom_price': false,
		'is_external_menu_enabled': true,
		'is_print_ingredients': false,
		'is_service_charge_enabled': true,
		'is_standalone': false,
		'is_constructor': false,
		'constructor_type': null,
		'constructor_view': null,
		'print_with_main': false,
		'is_stock_controlled': false,
		'is_marking': false,
		'description': '',
		'image': '',
		'cooking_time': '',
		'weight': '',
		'barcode': '',
		'plu': '',
		'proteins': '',
		'fat': '',
		'carbohydrates': '',
		'calories': '',
		'showSmiles': false,
		'sizes': [],
		'ingredients': [],
		'params': {
			'tg_text': '',
			'tg_color': '',
			'tg_bg_color': '',
		},
	})
	const [calculateDirection, setCalculateDirection] = useState('right')
	const [categories, setCategories] = useState([])
	const [categoriesInitial, setCategoriesInitial] = useState([])
	const [productTypes, setProductTypes] = useState([])
	const [productSystemTypes, setProductSystemTypes] = useState([])
	const [wms, setWms] = useState([])
	const [menu, setMenu] = useState([])
	const [units, setUnits] = useState([])
	const [elements, setElements] = useState([])
	const [taxCategories, setTaxCategories] = useState([])
	const [productLabels, setProductLabels] = useState([])
	const [productCategories, setProductCategories] = useState([])
	const [fiscalCodes, setFiscalCodes] = useState([])
	const [fiscalPackageCodes, setFiscalPackageCodes] = useState([])
	const [elementsDuplicate, setElementsDuplicate] = useState([])
	const [ingredientsHistory, setIngredientsHistory] = useState([])
	const [preparationPlaces, setPreparationPlaces] = useState([])
	const [activeTab, setActiveTab] = useState(5)
	// Поиск по филиалам во вкладке «Продаётся на точках»: у сети их бывают сотни
	const [posSaleSearch, setPosSaleSearch] = useState('')
	const [mainActiveTab, setMainActiveTab] = useState(initialMainTab)
	const [isOpen, setIsOpen] = useState(false)
	const [modalFiscal, setModalFiscal] = useState({
		'bool': false,
		'search': '',
		'fiscalCodes': [],
		'fiscalPackageCodes': [],
	})
	const [modalIngredientsHistory, setModalIngredientsHistory] = useState(false)
	const [showModifierModal, setShowModifierModal] = useState(false)
	const [showAddModifierModal, setShowAddModifierModal] = useState(false)
	const [showIngredientParamsModal, setShowIngredientParamsModal] = useState({ 'bool': false, 'index': null, 'params': null })
	const [modalDeleteIngredient, setModalDeleteIngredient] = useState(false)
	const [modalDeleteAllIngredients, setModalDeleteAllIngredients] = useState(false)
	const [deleteModalIngredientParams, setDeleteModalIngredientParams] = useState({ 'id': 0, 'index': 0 })
	const [fiscalCode, setFiscalCode] = useState('')
	const [isImageDragOver, setIsImageDragOver] = useState(false)
	const [imageUrl, setImageUrl] = useState('')
	const [imageUrlLoading, setImageUrlLoading] = useState(false)
	const [modalElement, setModalElement] = useState(false)
	const [quickElementRowIndex, setQuickElementRowIndex] = useState(null)
	const [modalImportRecipe, setModalImportRecipe] = useState({ 'bool': false, 'products': [], 'product': null })
	const savingRef = useRef(false)

	// Заготовка уходит в техкарту, модификатор цепляется строкой к блюду —
	// ни размеры, ни свои модификаторы у них не читает ни один канал продаж
	const hasNoVariants = [2, 3].includes(Number(data.product_type_id))
	const posesSale = data.poses_sale ?? []
	const filteredPosesSale = posSaleSearch
		? posesSale.filter((item) => String(item.name ?? '').toLowerCase().includes(posSaleSearch.toLowerCase()))
		: posesSale
	const posesSaleSelected = posesSale.filter((item) => item.is_on_sale).length
	// Общий чекбокс шапки: отмечен, когда выбрано всё видимое; «частично» — когда часть
	const visiblePosesSelected = filteredPosesSale.filter((item) => item.is_on_sale).length
	const allVisiblePosesSelected = filteredPosesSale.length > 0 && visiblePosesSelected === filteredPosesSale.length
	const someVisiblePosesSelected = visiblePosesSelected > 0 && !allVisiblePosesSelected
	const sizesLocked = hasNoVariants && !data?.sizes?.length
	const modifiersLocked = hasNoVariants && !data?.modifiers?.length

	// Сменили тип на заготовку/модификатор, пока открыты «Размеры» или «Модификаторы»
	useEffect(() => {
		if ((sizesLocked && activeTab === 1) || (modifiersLocked && activeTab === 2)) {
			setActiveTab(5)
		}
	}, [sizesLocked, modifiersLocked, activeTab, data.id])

	async function createUpdate(e, saveContinue = false) {
		e.preventDefault()
		// Второй клик, пока первый запрос в пути, отправил бы те же строки состава без id ещё раз
		if (savingRef.current) return
		savingRef.current = true
		try {
			await saveProduct(saveContinue)
		} finally {
			savingRef.current = false
		}
	}

	async function saveProduct(saveContinue) {
		var sendData = { ...data }
		sendData.ingredients = data.ingredients
		sendData.prices = data.prices
		sendData.error = false
		sendData.cooking_time = inputRef?.current?.inputElement?.value
		sendData.pos_id = posId
		delete sendData.in_product
		if (Array.isArray(sendData.ingredients)) {
			sendData.ingredients = sendData.ingredients.map((ingredient) => {
				const next = { ...ingredient }
				const typeId = next.product_type_id ?? next.in_product
				next.product_type_id = typeId
				// product_ingredients в бэке пока пишет in_product
				next.in_product = typeId
				return next
			})
		}

		if (sendData?.sizes?.length > 0) { // Если есть размеры цены обязательные
			for (let i = 0; i < sendData?.sizes.length; i++) {
				if (sendData?.sizes[i]['price'] === '' || sendData?.sizes[i]['sale_price'] === '') {
					sendData.error = true
				}
			}
		}

		var response;
		if (sendData.id) {
			response = await PUT('/products', sendData, { loader: true })
		} else {
			response = await POST('/products', sendData, { loader: true })
		}

		if (httpOk(response)) {
			// «Продаётся на точках» — отдельный регистр (прейскурант), а не поле карточки
			if (sendData.id && data.poses_sale?.length) {
				await PUT('/products-pos-sale', {
					product_id: sendData.id,
					poses: data.poses_sale.map((item) => ({
						pos_id: item.pos_id,
						is_on_sale: !!item.is_on_sale,
					})),
				}, { notification: false })
			}

			notifyProductsChanged()

			if (!saveContinue) {
				if (embedded && onClose) onClose()
				else if (sendData.id) {
					// Редактирование: остаёмся на карточке (тост «Успешно» показывает сам PUT).
					// Перечитываем, чтобы новые строки состава получили id — иначе следующее
					// «Сохранить» вставит их ещё раз
					await getData()
				} else if (response.data?.id) {
					// Создание: переходим на карточку созданного товара, чтобы следующее «Сохранить» обновляло его, а не плодило дубли
					setData({ ...data, id: response.data.id })
					notify(t('success'), 'success')
					navigate(`/products/update/${posId}/${response.data.id}`, { replace: true })
				} else navigate('/products')
			} else {
				setData({
					...data,
					'name': '',
					'sale_price': '',
					'product_type_id': 1,
					'unit_id': 1,
					'status': 1,
					'write_off_type_id': 1,
					'description': '',
					'image': '',
					'cooking_time': '',
					'showSmiles': false,
					'sizes': [],
					'ingredients': [],
				})
			}
		}
	}

	// Карточку открыли из списка в новой вкладке (истории нет) — закрываем её, иначе обычный переход к списку
	function closeTabOrBack(e) {
		if (window.history.length > 1) return
		e.preventDefault()
		window.close()
		setTimeout(() => navigate('/products'), 150)
	}

	async function createFiscalCode() {
		const response = await POST('/products-fiscalization', {
			'fiscal_code': fiscalCode
		}, { loader: true })

		if (httpOk(response)) {
			setFiscalCode('')
			var response2 = await GET(`/helper/fiscal-codes`, {}, { loader: true })
			if (httpOk(response2)) {
				setFiscalCodes(response2.data)
				notify(t('success'), 'success')
			}
		}
	}

	function addSizes() {
		var dataCopy = { ...data }

		var size = {
			'name': '',
			'price': '',
			'sale_price': '',
			'is_default_size': '',
		}

		if (!dataCopy.sizes) {
			dataCopy.sizes = []
		}

		dataCopy.sizes.push(size)
		setData(dataCopy)
	}

	function addIngredients() {
		var dataCopy = { ...data }
		var ingredient = {
			'element_id': null,
			'name': '',
			'unit_id': '',
			'unit_name': '',
			'second_unit_id': '',
			'price': '',
			'quantity': '',
			'loss': '',
			'product_type_id': '',
			'units': [],
			'changeable': true,
			'params': {
				"hall": true,
				"takeaway": true,
				"delivery": true,
			},
		}

		if (dataCopy.ingredients) {
			dataCopy.ingredients.push(ingredient)
			setData(dataCopy)
		}
	}

	async function toggleModalImportRecipe(bool = false) {
		if (!bool) {
			setModalImportRecipe({ 'bool': false, 'products': [], 'product': null })
			return
		}

		const response = await GET('/helper/products-with-recipe', {}, { loader: true })
		if (httpOk(response)) {
			// Сам себе источником быть не может
			const products = response.data.filter(item => String(item.id) !== String(data.id))
			setModalImportRecipe({ 'bool': true, 'products': products, 'product': null })
		}
	}

	// Строки техкарты другого товара добавляются как новые (без id): их можно поправить до сохранения,
	// а в базу они попадут обычным «Сохранить» — бэк вставляет строки без id
	async function importRecipe() {
		const source = modalImportRecipe.product
		if (!source) return

		const response = await GET(`/products/${posId}/${source.id}`, {}, { loader: true })
		if (!httpOk(response)) return

		const dataCopy = { ...data }
		const existingIds = (dataCopy.ingredients || []).filter(item => item.element_id).map(item => String(item.element_id))
		// Пустые строки, добавленные кнопкой «Добавить», импорт заменяет
		const ingredients = (dataCopy.ingredients || []).filter(item => item.element_id)

		let skipped = 0
		for (const ingredient of (response.data.ingredients || [])) {
			if (existingIds.includes(String(ingredient.element_id)) || String(ingredient.element_id) === String(data.id)) {
				skipped++
				continue
			}

			const next = { ...ingredient }
			delete next.id
			delete next.product_id
			delete next.created_at
			// numeric из БД приходит строкой с хвостом нулей — в редактируемом инпуте это мешает
			for (const key of ['quantity', 'loss_cold', 'loss_hot', 'weight_gross', 'weight_net', 'weight_finished']) {
				next[key] = (ingredient[key] === null || ingredient[key] === undefined || ingredient[key] === '') ? '' : Number(ingredient[key])
			}
			next.product_type_id = ingredient.product_type_id ?? ingredient.in_product
			next.changeable = !!ingredient.changeable
			next.exist_second_unit_id = !!ingredient.second_unit_id
			next.units = [ingredient.second_unit_id
				? { 'id': ingredient.second_unit_id, 'name': ingredient.second_unit_name }
				: { 'id': ingredient.unit_id, 'name': ingredient.unit_name }
			]
			ingredients.push(next)
			existingIds.push(String(ingredient.element_id))
		}

		dataCopy.ingredients = ingredients
		calculateCostPrice(dataCopy)
		toggleModalImportRecipe(false)

		if (skipped > 0) {
			notify(`${t('recipe_import_skipped')}: ${skipped}`)
		}
	}

	async function addModifierToProduct(productModifiers) {
		var sendData = []
		for (let i = 0; i < productModifiers.modifiers.length; i++) {
			sendData.push({
				'product_id': data.id,
				'product_modifier_id': productModifiers.modifiers[i]['id']
			})
		}

		await POST('/products-modifier', sendData, { loader: true })
		setShowAddModifierModal(false)
		getData()
	}

	async function toggleModalIngredientsHistory(bool = false) {
		if (bool) {
			var response = await GET(`/products-ingredients-history/${id}`, {}, { loader: true })
			if (httpOk(response)) {
				setIngredientsHistory(response.data)
			}
		}

		setModalIngredientsHistory(bool)
	}

	function handleSelectProductType(option) {
		var sale_price = ''
		if (option.id === 2 || option.id === 5) sale_price = 0

		setData({
			...data,
			'product_type_id': option.id,
			'product_system_type_id': null,
			'price': sale_price,
			'sale_price': sale_price,
		})
	}

	function handleIngridientChange(e, index) {
		var dataCopy = { ...data }

		if (e.target.type === 'number' || e.target.type === 'text') {
			dataCopy.ingredients[index][e.target.name] = e.target.value
		}

		if (e.target.type === 'checkbox') {
			dataCopy.ingredients[index][e.target.name] = e.target.checked
		}

		if (
			e.target.name === 'quantity' ||
			e.target.name === 'weight_net' ||
			e.target.name === 'weight_finished' ||
			e.target.name === 'loss_cold' ||
			e.target.name === 'loss_hot'
		) {
			calculateWeight(index, dataCopy)
		} else {
			setData(dataCopy)
		}
	}

	function toggleModalDeleteIngredient(item, index) {
		if (item.id) {
			setDeleteModalIngredientParams({ 'id': item.id, 'index': index })
			setModalDeleteIngredient(true)
		} else {
			var dataCopy = { ...data }
			dataCopy.ingredients.splice(index, 1)

			calculateCostPrice(dataCopy)
		}
	}

	async function deleteIngredient() {
		await DELETE(`/products-ingredient/${deleteModalIngredientParams.id}`, {}, { loader: true })
		var dataCopy = { ...data }
		dataCopy.ingredients.splice(deleteModalIngredientParams.index, 1)
		calculateCostPrice(dataCopy)
		setModalDeleteIngredient(false)
	}

	async function deleteAllIngredients() {
		if (id) {
			await DELETE(`/products-ingredients-all/${id}`, {}, { loader: true })
		}

		var dataCopy = { ...data }
		dataCopy.ingredients = []
		calculateCostPrice(dataCopy)
		setModalDeleteAllIngredients(false)
	}

	async function deleteModifier(item) {
		var response = await DELETE('/modifiers-product', { 'id': item.id }, { loader: true })

		if (httpOk(response)) {
			var dataCopy = { ...data }
			dataCopy.modifiers = dataCopy.modifiers.filter(element => element.id !== item.id);
			setData(dataCopy)
		}
	}

	function handleChange(e) {
		var dataCopy = { ...data }
		dataCopy[e.target.name] = e.target.value

		calculateCostPrice(dataCopy)
	}

	async function uploadImageFile(file) {
		if (!file) return

		if (!file.type?.startsWith('image/')) {
			notify(t('image2'))
			return
		}

		const imgSize = file.size / (1024 * 1024);

		if (imgSize > 25) { // Проверка, если размер больше 25 МБ
			notify("Размер файла не должен превышать 25 МБ.");
			return;
		}

		try {
			// Если прошла валидация, формируем данные и отправляем запрос
			let formData = new FormData();
			if (data.id) formData.append("id", data.id);
			formData.append("image", file);
			formData.append("db_name", 'products');
			formData.append("column_name", 'image');
			formData.append("path", 'products');

			let response;
			if (!data.id) {
				response = await FILE('/upload-image', formData, {}, { loader: true });
			} else {
				response = await FILE(`/update-image`, formData, {}, { loader: true });
			}

			if (httpOk(response)) {
				setData({ ...data, image: response.data.url });
			}
		} catch (error) {
			notify(error.message);
		}
	}

	async function fileUpload(e) {
		await uploadImageFile(e.target.files[0])
	}

	async function handleImageDrop(e) {
		e.preventDefault()
		setIsImageDragOver(false)
		await uploadImageFile(e.dataTransfer.files[0])
	}

	async function loadImageFromUrl() {
		const url = imageUrl.trim()
		if (!url) return

		try {
			new URL(url)
		} catch (error) {
			notify(t('image_url_invalid'))
			return
		}

		const payload = {
			url,
			db_name: 'products',
			column_name: 'image',
			path: 'products',
		}

		if (data.id) payload.id = data.id

		setImageUrlLoading(true)
		try {
			const response = await POST('/import-image-from-url', payload, { loader: true })
			if (httpOk(response)) {
				setData({ ...data, image: response.data.url })
				setImageUrl('')
				notify(t('success'), 'success')
			}
		} catch (error) {
			notify(t('image_url_load_failed'))
		} finally {
			setImageUrlLoading(false)
		}
	}

	async function handleImageUrlPaste(e) {
		const pasted = e.clipboardData?.getData('text')?.trim()
		if (!pasted) return

		setImageUrl(pasted)
	}

	/**
	 * Цена за 1 ед. ингредиента в колонке «Кол-во».
	 * Заготовка: cost_price в БД за весь выход (products.quantity = 5/10/20 кг) → делим на выход.
	 * Вторая ед. у элемента (бут/шт): cost_price уже за 1 шт; pe.quantity — только вес для брутто/нетто.
	 */
	function getIngredientUnitPrice(item) {
		const averagePrice = Number(item?.average_price) || 0
		const isPrep = Number(item?.product_type_id ?? item?.in_product) === 2
		if (item?.second_unit_id && !isPrep) {
			return averagePrice
		}
		const yieldQty = Number(item?.final_quantity) > 0 ? Number(item.final_quantity) : 1
		return averagePrice / yieldQty
	}

	function getIngredientCostPrice(item) {
		const quantity = Number(item?.quantity) || 0
		return getIngredientUnitPrice(item) * quantity
	}

	function calculateCostPrice(dataCopyFromDelete) {
		//debugger
		var dataCopy = { ...data }

		if (dataCopyFromDelete) {
			dataCopy = dataCopyFromDelete
		} else {
			dataCopy = { ...data }
		}

		// Есть состав — считаем себестоимость из ингредиентов.
		// Нет состава — берём текущую себестоимость продукта (ручную/приходную) из average_price.
		if (dataCopy.ingredients.length) {
			dataCopy['cost_price'] = 0
		} else {
			dataCopy['cost_price'] = Number(dataCopy['average_price']) || 0
		}
		dataCopy['overall_weight_gross'] = 0
		dataCopy['overall_weight_net'] = 0
		dataCopy['overall_weight_finished'] = 0
		for (let i = 0; i < dataCopy.ingredients.length; i++) {
			if (!dataCopy.ingredients[i]['quantity']) {
				dataCopy.ingredients[i]['quantity'] = ''
			}

			if (dataCopy.ingredients[i]['weight_gross']) {
				dataCopy['overall_weight_gross'] += Number(dataCopy.ingredients[i]['weight_gross'])
			}

			if (dataCopy.ingredients[i]['weight_net']) {
				dataCopy['overall_weight_net'] += Number(dataCopy.ingredients[i]['weight_net'])
			}

			if (dataCopy.ingredients[i]['weight_finished']) {
				dataCopy['overall_weight_finished'] += Number(dataCopy.ingredients[i]['weight_finished'])
			}

			const lineCost = getIngredientCostPrice(dataCopy.ingredients[i])
			dataCopy['cost_price'] = dataCopy['cost_price'] + lineCost
			dataCopy.ingredients[i]['cost_price'] = lineCost
		}

		setData(dataCopy)
	}

	function toggleIngredientParamsModal(bool = false, index, item = {}) {
		if (bool) {
			setShowIngredientParamsModal({
				...showIngredientParamsModal,
				'bool': bool,
				'index': index,
				'params': item.params,
				'note': item.note,
				'changeable': item.changeable,
				'write_off_type_id': item.write_off_type_id,
			})
		}
		if (!bool) {
			setShowIngredientParamsModal({ ...showIngredientParamsModal, 'bool': bool, 'index': null, 'params': null })
		}
	}

	function handleIngredientParamsChange(e, type) {
		var showIngredientParamsModalCopy = { ...showIngredientParamsModal }

		if (type === 'checkbox' && (
			e.target.name === "hall" ||
			e.target.name === "takeaway" ||
			e.target.name === "delivery"
		)) {
			showIngredientParamsModalCopy.params[e.target.name] = e.target.checked
		}

		if (type === 'checkbox' && e.target.name === "changeable") {
			showIngredientParamsModalCopy.changeable = e.target.checked
		}

		if (type === 'text') {
			showIngredientParamsModalCopy.note = e.target.value
		}

		setShowIngredientParamsModal(showIngredientParamsModalCopy)
	}

	function saveIngredientParams() {
		var dataCopy = { ...data }
		dataCopy.ingredients[showIngredientParamsModal.index]['params'] = showIngredientParamsModal.params
		dataCopy.ingredients[showIngredientParamsModal.index]['note'] = showIngredientParamsModal.note
		dataCopy.ingredients[showIngredientParamsModal.index]['changeable'] = showIngredientParamsModal.changeable
		dataCopy.ingredients[showIngredientParamsModal.index]['write_off_type_id'] = showIngredientParamsModal.write_off_type_id

		setData(dataCopy)
		toggleIngredientParamsModal()
	}

	function handleParamsChange(key, value) {
		var dataCopy = { ...data }
		dataCopy.params[key] = value
		setData(dataCopy)
	}

	function handleChangeModifier(e, index) {
		var dataCopy = { ...data }
		if (e.target.type === 'number') {
			dataCopy.modifiers[index][e.target.name] = e.target.value

			if (e.target.name === 'min' && Number(dataCopy.modifiers[index]['min']) > 0) {
				dataCopy.modifiers[index]['required'] = true
			}
			if (
				e.target.name === 'min' &&
				(
					!dataCopy.modifiers[index]['min'] ||
					Number(dataCopy.modifiers[index]['min']) === 0
				)
			) {
				dataCopy.modifiers[index]['required'] = false
			}
		}

		if (e.target.type === 'checkbox') {
			dataCopy.modifiers[index][e.target.name] = !dataCopy.modifiers[index][e.target.name]
		}
		setData(dataCopy)
	}

	function handleChangeModifierProduct(e, parentIndex, index) {
		var dataCopy = { ...data }
		const field = e.target.name
		const child = dataCopy.modifiers[parentIndex]['products'][index]
		child[field] = e.target.type === 'checkbox' ? e.target.checked : e.target.value

		// «Входит в цену» относится к штукам по умолчанию: включили, а их нет — ставим одну
		const includedWithoutDefault = field === 'base_price_included' && child[field] && !Number(child.default_quantity)
		if (includedWithoutDefault) child.default_quantity = 1

		// Сумма default_quantity детей → default_quantity группы (макс группы задаётся отдельно)
		if (field === 'default_quantity' || includedWithoutDefault) {
			dataCopy.modifiers[parentIndex].default_quantity = 0
			for (let i = 0; i < dataCopy.modifiers[parentIndex]['products'].length; i++) {
				if (dataCopy.modifiers[parentIndex]['products'][i]['default_quantity']) {
					dataCopy.modifiers[parentIndex].default_quantity +=
						Number(dataCopy.modifiers[parentIndex]['products'][i]['default_quantity'])
				}
			}
		}

		setData(dataCopy)
	}

	// Массовое переключение: применяем к тому, что сейчас видно в списке,
	// иначе «Выбрать все» при активном поиске молча тронет и скрытые филиалы.
	function setPosesSale(isOnSale) {
		const visible = new Set(filteredPosesSale.map((item) => item.pos_id))

		setData({
			...data,
			'poses_sale': (data.poses_sale ?? []).map((pos) => (
				visible.has(pos.pos_id) ? { ...pos, 'is_on_sale': isOnSale } : pos
			)),
		})
	}

	function togglePosSale(posId, isOnSale) {
		setData({
			...data,
			'poses_sale': (data.poses_sale ?? []).map((pos) => (
				pos.pos_id === posId ? { ...pos, 'is_on_sale': isOnSale } : pos
			)),
		})
	}

	function handleActiveTab(value) {
		if ((sizesLocked && value === 1) || (modifiersLocked && value === 2)) return

		setActiveTab(value)
	}

	function toggleModalFiscalization(bool = false) {
		setModalFiscal({
			'bool': bool,
			'search': '',
			'fiscalCodes': [],
			'fiscalPackageCodes': [],
		})
	}

	async function selectCategory(option) {
		setIsOpen(!isOpen)
		setData({ ...data, 'category_id': option?.id, 'category_name': option?.name, })
	}

	// REACTSELECT LOGIC
	function handleSelectSearch(value) {
		if (!value) {
			setElements([...elementsDuplicate])
			return
		}
		var elementsDuplicateCopy = [...elementsDuplicate]

		var filteredItems = elementsDuplicateCopy.filter(item => item.name.toLowerCase().includes(value.toLowerCase()))
		if (filteredItems[0]) {
			filteredItems[0]['selected'] = true
		}
		setElements(filteredItems)
	}

	function handleSelectElement(option, index) {
		var dataCopy = { ...data }

		var found = !!dataCopy.ingredients.find(item => item.element_id === option.id)
		if (found) {
			dataCopy.ingredients[index]['element_id'] = ''
			notify(`Ингридиент уже добавлен`)
			return
		}
		dataCopy.ingredients[index] = {
			'changeable': true,
			'params': {
				"hall": true,
				"takeaway": true,
				"delivery": true,
			},
		}

		dataCopy.ingredients[index]['quantity'] = ""
		dataCopy.ingredients[index]['loss_cold'] = ""
		dataCopy.ingredients[index]['loss_hot'] = ""
		dataCopy.ingredients[index]['weight_net'] = ""
		dataCopy.ingredients[index]['weight_finished'] = ""
		dataCopy.ingredients[index]['weight_gross'] = ""

		dataCopy.ingredients[index]['units'] = []
		dataCopy.ingredients[index]['element_id'] = option.id
		dataCopy.ingredients[index]['name'] = option.name
		dataCopy.ingredients[index]['average_price'] = option.average_price
		dataCopy.ingredients[index]['pos_id'] = option.pos_id
		dataCopy.ingredients[index]['loss'] = option.loss
		dataCopy.ingredients[index]['product_type_id'] = option.product_type_id ?? option.in_product
		dataCopy.ingredients[index]['product_type_name'] = option.product_type_name
		dataCopy.ingredients[index]['final_quantity'] = option.final_quantity
		if (option.second_unit_id) {
			dataCopy.ingredients[index]['second_unit_id'] = option.second_unit_id
			dataCopy.ingredients[index]['second_unit_quantity'] = option.final_quantity
			dataCopy.ingredients[index]['second_unit_name'] = option.second_unit_name

			// auto select unit
			dataCopy.ingredients[index]['exist_second_unit_id'] = true
			dataCopy.ingredients[index]['unit_id'] = option.unit_id
			dataCopy.ingredients[index]['unit_name'] = option.unit_name
			// auto select unit
			dataCopy.ingredients[index]['units'].push({
				'id': option.second_unit_id,
				'name': option.second_unit_name
			})
		} else {
			// auto select unit
			dataCopy.ingredients[index]['unit_id'] = option.unit_id
			dataCopy.ingredients[index]['exist_second_unit_id'] = false
			dataCopy.ingredients[index]['unit_name'] = option.unit_name
			// auto select unit

			dataCopy.ingredients[index]['units'].push({
				'id': option.unit_id,
				'name': option.unit_name
			})
		}

		// Выход элемента в кг/литрах (помидор 0.85) — холодные потери строки, 15 %
		const yieldRatio = Number(option.final_quantity)
		if (option.second_unit_id && isWeightUnitElement(option) && yieldRatio > 0 && yieldRatio < 1) {
			dataCopy.ingredients[index]['loss_cold'] = Number(((1 - yieldRatio) * 100).toFixed(2))
		}

		setData(dataCopy)
	}

	function closeReactSelect() {
		setTimeout(() => {
			var elementsCopy = [...elements]
			for (let i = 0; i < elementsCopy.length; i++) {
				elementsCopy[i]['selected'] = false
			}
			if (elementsCopy[0] && elementsCopy[0]['selected'])
				elementsCopy[0]['selected'] = true
			setElements(elementsCopy)
		}, 100);
	}

	function handleTableKeyDown(event) {
		event.stopPropagation();
		var currentIndex = elements.findIndex(el => el.selected);
		if (currentIndex >= 0) {
			var elementsCopy = [...elements]
			elementsCopy[currentIndex].selected = false
			switch (event.key) {
				case "ArrowUp":
					if (currentIndex === 0) {
						elementsCopy[elements.length - 1].selected = true
						currentIndex = elements.length - 1
					} else {
						elementsCopy[currentIndex - 1].selected = true
					}
					scrollRef.current.scrollTop = (currentIndex * 29.6) - 59.2
					break;
				case "ArrowDown":
					if (currentIndex === elements.length - 1) {
						elementsCopy[0].selected = true
						currentIndex = 0
					} else {
						elementsCopy[currentIndex + 1].selected = true
					}
					scrollRef.current.scrollTop = (currentIndex * 29.6)
					break;
				default: break;
			}

			setElements(elementsCopy)
		}
	}
	// REACTSELECT LOGIC

	async function handleSelectTab() {
		setMainActiveTab(3)

		if (!fiscalCodes.length) {
			var response = await GET(`/helper/fiscal-codes`, {}, { loader: true })
			if (httpOk(response)) setFiscalCodes(response.data)
		}
	}

	function handleSelectFiscalCode(option, changePackage = true) {
		if (changePackage) {
			if (!option) {
				setData({ ...data, 'fiscal_code': null, 'fiscal_package_code': null })
				return
			}

			setData({ ...data, 'fiscal_code': option.id, 'fiscal_package_code': null })
			setFiscalPackageCodes(option.units)
		}

		setFiscalPackageCodes(option.units)
	}

	function handleSelectFiscalPackageCode(option) {
		if (!option) {
			setData({ ...data, 'fiscal_package_code': null })
			return
		}

		setData({ ...data, 'fiscal_package_code': option.id })
	}

	async function getData() {
		var response = await GET(`/products/${posId}/${id}`, {}, { loader: true })
		if (httpOk(response)) {
			response = response.data
			var responseUnits = await GET('/products-units')
			if (httpOk(responseUnits)) {
				setUnits(responseUnits.data)
			}

			for (let i = 0; i < response?.ingredients?.length; i++) {
				if (response.ingredients[i]['second_unit_quantity']) {
					if (response.ingredients[i]['loss'] && !response.ingredients[i]['id']) {
						response.ingredients[i]['quantity'] =
							response.ingredients[i]['second_unit_quantity'] -
							response.ingredients[i]['loss']
					}
				}
			}

			if (response) {
				const boolFields = [
					'status', 'is_divisible', 'is_divisible', 'is_custom_price',
					'is_external_menu_enabled',
					'is_print_ingredients', 'is_service_charge_enabled', 'is_standalone', 'is_constructor',
					'print_with_main', 'is_stock_controlled', 'is_marking',
				]
				for (let i = 0; i < boolFields.length; i++) {
					const key = boolFields[i]
					response[key] = !!response[key]
				}
				response.product_type_id = Number(response.product_type_id ?? response.in_product ?? 1)
				delete response.in_product
				if (Array.isArray(response.ingredients)) {
					response.ingredients = response.ingredients.map((ingredient) => ({
						...ingredient,
						product_type_id: ingredient.product_type_id ?? ingredient.in_product,
					}))
				}

				response.weight = response.weight ?? ''
				response.barcode = response.barcode ?? ''
				response.plu = response.plu ?? ''
				response.proteins = response.proteins ?? ''
				response.fat = response.fat ?? ''
				response.carbohydrates = response.carbohydrates ?? ''
				response.calories = response.calories ?? ''
				response.description = response.description ?? ''
				response.cooking_time = response.cooking_time ?? ''
				response.sale_price = response.sale_price ?? ''
				response.params = response.params || {
					'tg_text': '',
					'tg_color': '',
					'tg_bg_color': '',
				}
				calculateCostPrice(response)
			}

			var responseFiscalPackages = await GET('/helper/fiscal-codes')
			if (httpOk(responseFiscalPackages)) {
				setFiscalCodes(responseFiscalPackages.data)
				if (response.fiscal_code) {
					var option = responseFiscalPackages.data.find(item => item.id === response.fiscal_code)
					if (option?.id) setFiscalPackageCodes(option.units)
				}
			}
		}
	}

	async function getMenu() {
		var response = await GET('/helper/menu')
		if (httpOk(response)) setMenu(response.data)
	}

	async function getElements() {
		const response = await GET(`/products-elements/${posId}`)
		if (httpOk(response)) {
			setElements(response.data)
			setElementsDuplicate(response.data)
			return response.data
		}
		return []
	}

	function toggleModalElement(bool, rowIndex = null) {
		setQuickElementRowIndex(rowIndex)
		setModalElement(bool)
	}

	async function handleCreatedElement(element) {
		if (!element) return

		const freshElements = await getElements()
		const createdId = element.id ?? element.product_id
		const rowIndex = quickElementRowIndex

		if (rowIndex !== null && data.ingredients[rowIndex]) {
			const createdElement = freshElements.find(item => item.id === createdId)
			if (createdElement) {
				handleSelectElement(createdElement, rowIndex)
			}
		}

		setQuickElementRowIndex(null)
	}

	async function getUnits() {
		const response = await GET('/products-units')
		if (httpOk(response)) setUnits(response.data)
	}

	async function getPreparationPlaces() {
		const response = await GET(`/helper/preparation-places?pos_id=${posId}`)
		if (httpOk(response)) setPreparationPlaces(response.data)
	}

	async function getPoses() {
		var responsePrices = await GET('/helper/poses')
		if (httpOk(responsePrices)) {
			responsePrices = responsePrices.data
			for (let i = 0; i < responsePrices.length; i++) {
				responsePrices[i]['pos_id'] = responsePrices[i]['id']
				responsePrices[i]['cost_price'] = 0
				responsePrices[i]['price'] = ''
				responsePrices[i]['sale_price'] = ''
			}
		}
	}

	async function getWms() {
		const response = await GET(`/helper/wms?pos_id=${posId}`)
		if (httpOk(response)) {
			setWms(response.data)
			const result = response.data.find(item => item.parent_id === null);
			// Only default warehouse for create; on update avoid overwriting loaded product with stale state.
			if (result?.id && !id) {
				setData(prev => ({ ...prev, 'wms_id': result.id }))
			}
		}
	}

	async function getCategories() {
		var response = await GET('/helper/categories')
		response = response.data

		var arr = []
		for (let i = 0; i < response.length; i++) {
			response[i]['label'] = ` ${response[i]['name']}`
			response[i].child = false;
			if (response[i].parent_id) {
				const index = response.findIndex(x => x.id === response[i].parent_id)
				response[i].child = true;
				if (response[index]?.options && response[index]?.options?.length > 0) {
					response[index].options.push(response[i])
				} else {
					response[index].options = [response[i]]
				}
			} else {
				arr.push(response[i])
			}
		}

		setCategories(arr)
		setCategoriesInitial(response)
	}

	async function getTaxCategories() {
		const response = await GET(`/helper/tax-categories`)
		if (httpOk(response)) setTaxCategories(response.data)
	}

	async function getProductLabels() {
		const response = await GET('/product-labels')
		if (httpOk(response)) setProductLabels(response.data.data ?? [])
	}

	// Метка у товара — вкл/выкл; дата «до» необязательна («Новинка до 30.11»)
	function toggleProductLabel(labelId) {
		const labels = data.labels ?? []
		const exists = labels.some((item) => item.product_label_id === labelId)
		// На карточке у гостя помещаются только две метки — третью не даём выбрать
		if (!exists && labels.length >= PRODUCT_LABELS_MAX) return
		setData({
			...data,
			labels: exists
				? labels.filter((item) => item.product_label_id !== labelId)
				: [...labels, { product_label_id: labelId, ends_at: null }],
		})
	}

	function setProductLabelEndsAt(labelId, endsAt) {
		setData({
			...data,
			labels: (data.labels ?? []).map((item) => item.product_label_id === labelId ? { ...item, ends_at: endsAt || null } : item),
		})
	}

	async function getProductCategories() {
		const response = await GET(`/helper/categories`)
		if (httpOk(response)) setProductCategories(response.data)
	}

	async function getProductTypes() {
		const response = await GET(`/helper/product-types`)
		if (httpOk(response)) setProductTypes(response.data)
	}

	async function getProductSystemTypes() {
		const response = await GET(`/helper/product-system-types`)
		if (httpOk(response)) setProductSystemTypes(response.data)
	}

	const GroupHead = props => {
		return (
			<div style={{
				padding: '0',
				color: data.category_id === props.data.id ? '#fff' : 'var(--dark-text, #535277)',
				fontWeight: 'bold',
				backgroundColor: data.category_id === props.data.id ? '#2684FF' : 'transparent',
				fontSize: '1rem'
			}}
				onClick={() => selectCategory(props.data)}>
				<components.Option {...props} />
			</div>
		);
	};

	const Option = props => {
		return (
			<div style={{
				padding: props.data.child ? '0 0 0 20px' : '0',
				color: 'var(--dark-text, #535277)',
				fontWeight: props.data.parent_id === 0 ? 'bold' : 'normal',
				fontSize: '.9rem'
			}}>
				<components.Option {...props} />
			</div>
		);
	};

	function calculateWeight(index, sourceData = data) {
		// Создаем копию данных для соблюдения принципа immutability
		const dataCopy = { ...sourceData };
		const item = dataCopy.ingredients[index];

		// Коэффициент веса: 1 вторая ед. (шт) = final_quantity основной (кг/л). Без second_unit — 1.
		// У элемента в кг/литрах «kg» — выход, он уже в холодных потерях: брутто = кол-во
		const unitRatio = item.second_unit_id && !isWeightUnitElement(item)
			? Number(item.final_quantity || item.second_unit_quantity || 1)
			: 1;

		// Приводим потери к числам для безопасности расчетов
		const lossCold = Number(item.loss_cold || 0);
		const lossHot = Number(item.loss_hot || 0);

		if (calculateDirection === 'right') {
			/** * Направление ВПРАВО: Кол-во (штук) -> Брутто (кг/л) -> Нетто -> Готово */
			const quantity = Number(item.quantity || 0);

			// ИСПРАВЛЕНИЕ: Брутто = Кол-во штук * объем одной штуки (например, 1 * 0.75)
			const grossValue = quantity * unitRatio;
			item.weight_gross = Number(grossValue.toFixed(4));

			// Расчет Нетто от Брутто
			const netValue = grossValue - (grossValue * lossCold / 100);
			item.weight_net = Number(netValue.toFixed(4));

			// Расчет Готового продукта от Нетто
			const finishedValue = netValue - (netValue * lossHot / 100);
			item.weight_finished = Number(finishedValue.toFixed(4));
		}

		else if (calculateDirection === 'left') {
			/** * Направление ВЛЕВО: Из Готового -> Нетто -> Брутто -> Кол-во */
			const finished = Number(item.weight_finished || 0);
			let net = Number(item.weight_net || 0);

			// Если пользователь ввел итоговый вес, сначала восстанавливаем Нетто.
			if (finished > 0) {
				const hotLossMultiplier = 1 - (lossHot / 100);
				if (hotLossMultiplier > 0) {
					net = finished / hotLossMultiplier;
				} else {
					net = finished;
				}
				item.weight_net = Number(net.toFixed(4));
			}

			const coldLossMultiplier = 1 - (lossCold / 100);
			let grossValue;
			if (coldLossMultiplier > 0) {
				grossValue = net / coldLossMultiplier;
			} else {
				grossValue = net;
			}

			item.weight_gross = Number(grossValue.toFixed(4));
			const finalRoundedQuantity = Number((grossValue / unitRatio).toFixed(4));
			item.quantity = finalRoundedQuantity;
		}

		// Обновляем состояние через вашу функцию (зависит от вашего фреймворка, например setData или аналоги)
		// В конце вызываем расчет себестоимости на основе новых весов
		calculateCostPrice(dataCopy);
	}

	useEffect(() => {
		if (id) getData()
		if (!id) {
			getUnits()
			// При создании с таба (Mahsulot/Zagatovka/Modifikator) подставляем его тип из URL.
			const typeRaw = new URLSearchParams(location.search).get('product_type_id')
			const typeId = typeRaw !== null && typeRaw !== '' && !Number.isNaN(Number(typeRaw))
				? Number(typeRaw)
				: null
			if (typeId) {
				setData((prev) => ({
					...prev,
					product_type_id: typeId,
					sale_price: (typeId === 2 || typeId === 5) ? 0 : prev.sale_price,
					price: (typeId === 2 || typeId === 5) ? 0 : prev.price,
				}))
			}
		}

		getWms()
		getMenu()
		getPoses()
		getElements()
		getCategories()
		getProductTypes()
		getProductSystemTypes()
		getTaxCategories()
		getProductLabels()
		getProductCategories()
		getPreparationPlaces()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{!embedded && <div className="page-header d-flex mb-2">
				<div className="d-flex">
					<h4 className="vertical-center">
						{t('product')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						{!id ?
							<h6>{t('create')}</h6>
							:
							<h6>{t('edit')}</h6>
						}
					</div>
					{id &&
						<Fragment>
							<div className="vertical-center mt-1">
								<i className="uil uil-angle-double-right fz-20"></i>
							</div>
							<div className="vertical-center mt-1">
								{data.name}
							</div>
						</Fragment>
					}
				</div>
			</div>}

			<div className="card">
				<div className="card-body">
					<form autoComplete="off">
						{/* одна линия вкладок: слева — вкладки карточки, справа — разделы правой колонки «Главной» */}
						<div className="row mb-2">
							<div className="col-md-5">
								<div className="tabs tabs-sm">
									<ul>
										<li className={mainActiveTab === 1 ? 'active' : ''}
											onClick={() => setMainActiveTab(1)}>
											<span className="link">{t('main')}</span>
										</li>
										<li className={mainActiveTab === 2 ? 'active ' : ''}
											onClick={() => setMainActiveTab(2)}>
											<span className="link">{t('ingredients')}</span>
										</li>
										<li className={mainActiveTab === 3 ? 'active ' : ''}
											onClick={() => handleSelectTab(3)}>
											<span className="link">{t('fiscalization')}</span>
										</li>
										{!!data.id && data.poses_sale?.length > 1 &&
											<li className={mainActiveTab === 4 ? 'active ' : ''}
												onClick={() => setMainActiveTab(4)}>
												<span className="link">{t('sold_at_poses')}</span>
											</li>
										}
									</ul>
								</div>
							</div>
							<div className={`col-md-7 ${mainActiveTab === 1 ? '' : 'd-none'}`}>
								<div className="tabs tabs-sm">
									<ul>
										<li className={activeTab === 5 ? 'active ' : ''}
											onClick={() => handleActiveTab(5)}>
											<span className="link">{t('product_basic')}</span>
										</li>
										<li className={activeTab === 1 ? 'active ' : ''}
											onClick={() => handleActiveTab(1)}>
											<span className={`link ${sizesLocked ? 'disabled' : ''}`}>
												{t('sizes')}
											</span>
										</li>
										{id &&
											<li className={activeTab === 2 ? 'active' : ''}
												onClick={() => handleActiveTab(2)}>
												<span className={'link ' + (modifiersLocked ? 'disabled ' : '')}>
													{t('modifiers')}
												</span>
											</li>
										}
										{(data.id) &&
											<li className={activeTab === 3 ? 'active' : ''}
												onClick={() => handleActiveTab(3)}>
												<span className="link">{t('in_products')}</span>
											</li>
										}
										<li className={activeTab === 4 ? 'active ' : ''}
											onClick={() => handleActiveTab(4)}>
											<span className="link">
												{t('showcase')}
											</span>
										</li>
									</ul>
								</div>
							</div>
						</div>
						<div className={`row mb-5 ${mainActiveTab === 1 ? '' : 'd-none'}`}>
							<div className="col-md-5">
								<div className="flex-gap10">
									<div className="w-50">
										<div
											className="con-upload"
											onDragOver={(e) => {
												e.preventDefault()
												setIsImageDragOver(true)
											}}
											onDragLeave={() => setIsImageDragOver(false)}
											onDrop={handleImageDrop}>
											<div className="con-img-upload">
												{data.image &&
													<div
														className="img-upload"
														style={{
															border: isImageDragOver ? '2px dashed #5b73e8' : undefined,
															background: isImageDragOver ? 'var(--dark-tint-primary, #eef2ff)' : undefined,
														}}>
														<button type="button" className="btn-x-file"
															onClick={() => setData({ ...data, 'image': '' })}>
															<i className="uil-times"></i>
														</button>
														<img src={`https://backend.mison.uz/uploads/products/${data.image}`}
															style={{ maxWidth: 'none', maxHeight: '100%' }} alt={data.image} />
													</div>
												}
												{!data.image &&
													<div
														className="con-input-upload"
														style={{
															border: isImageDragOver ? '2px dashed #5b73e8' : undefined,
															background: isImageDragOver ? 'var(--dark-tint-primary, #eef2ff)' : undefined,
														}}>
														<input type="file" onChange={(e) => fileUpload(e)} />
														<span className="text-input fz14">
															{t('image2')}
														</span>
													</div>
												}
											</div>
										</div>
									</div>
									<div className="w-50">
										<div className="form-group">
											<label>{t("name2")}<span className="required-mark">*</span></label>
											<div className="position-relative">
												<input type="text" className="form-control" name="name" autoFocus
													value={data.name ?? ''}
													onChange={(e) => setData({ ...data, 'name': e.target.value })} />
												<div className="input-smile-icon"
													onClick={() => setData({ ...data, 'showSmiles': !data.showSmiles })}>🙂</div>
												{data.showSmiles &&
													<div className="input-smile-wrapper">
														{smiles.map((item, index) => (
															<div key={index} onClick={() => setData({ ...data, 'name': data.name + smiles[index] })}>
																{smiles[index]}
															</div>
														))}
													</div>
												}
											</div>
										</div>
										<div className="form-group">
											<label>{t("menu")}</label>
											<Select
												isClearable
												options={menu}
												value={menu.find(option => option.id === data.menu_id) || null}
												onChange={(option) => setData({ ...data, 'menu_id': option ? option.id : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
												menuPortalTarget={document.body}
												styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
											/>
										</div>
										<div className="form-group w-100">
											<label>{t("preparation_places")}</label>
											<Select
												isClearable
												options={preparationPlaces}
												value={preparationPlaces.find(option => option.id === data.preparation_place_id)}
												onChange={(option) => setData({ ...data, 'preparation_place_id': option ? option.id : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
												menuPortalTarget={document.body}
												styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
											/>
										</div>
									</div>
								</div>
								<div className="image-url-import mt-2">
									<div className="input-group">
										<span className="input-group-text">
											<i className="uil uil-link"></i>
										</span>
										<input
											type="url"
											className="form-control"
											placeholder={t('image_url_placeholder')}
											value={imageUrl}
											onChange={(e) => setImageUrl(e.target.value)}
											onPaste={handleImageUrlPaste}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault()
													loadImageFromUrl()
												}
											}}
										/>
										<button
											type="button"
											className="btn btn-primary"
											disabled={!imageUrl.trim() || imageUrlLoading}
											onClick={loadImageFromUrl}>
											{imageUrlLoading ? '...' : t('image_url_apply')}
										</button>
									</div>
								</div>
								<div className="flex-gap10">
									<div className="form-group w-100">
										<label>{t('type')}<span className="required-mark">*</span></label>
										<Select
											isDisabled={data.id}
											options={productTypes}
											value={productTypes.find(option => option.id === data.product_type_id)}
											onChange={(option) => handleSelectProductType(option)}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
									<div className="form-group w-100">
										<label>{t('system_types')}</label>
										<Select
											isDisabled={!(data.product_type_id === 5 && !data.id)}
											options={productSystemTypes}
											value={productSystemTypes.find(option => option.id === data.product_system_type_id) || null}
											onChange={(option) => setData({ ...data, 'product_system_type_id': option.id })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
								</div>
								{data.product_type_id === 3 &&
									<div className="form-check">
										<input id="print_with_main" className="form-check-input" type="checkbox"
											checked={!!data.print_with_main}
											onChange={(e) => setData({ ...data, 'print_with_main': e.target.checked })}
										/>
										<label className="form-check-label" htmlFor="print_with_main">
											{t('print_with_main_product')}
										</label>
									</div>
								}
								<div className="flex-gap10">
									<div className="form-group w-100">
										<label>{t("cost_price")}</label>
										<input type="text" className="form-control" name="cost_price"
											disabled
											value={formatMoney(data.cost_price) ?? ''}
											onChange={() => { }} />
									</div>
									<div className="form-group w-100">
										<label>{t('sale_price')}<span className="required-mark">*</span></label>
										<input
											disabled={data.product_type_id === 5}
											type="number"
											className="form-control"
											name="sale_price"
											value={data.sale_price ?? ''}
											onChange={(e) => handleChange(e)} />
									</div>
									<div className="form-group w-100">
										<label>{t('income')}<span className="required-mark">*</span></label>
										<input type="text"
											disabled
											className="form-control"
											value={formatMoney(data.sale_price - data.cost_price) ?? ''} />
									</div>
								</div>
								{!!data.id && Number(data.sale_price_overridden_pos_count) > 0 &&
									<div className="form-group">
										<div className="text-muted table-control-icon">
											{t('sale_price_pos_scope', {
												selected: data.sale_price_overridden_pos_count,
												total: data.sale_price_pos_count,
											})}
										</div>
										<div className="form-check">
											<input type="checkbox"
												className="form-check-input"
												id="apply_sale_price_to_all"
												checked={!!data.apply_sale_price_to_all}
												onChange={(e) => setData({ ...data, 'apply_sale_price_to_all': e.target.checked })} />
											<label className="form-check-label" htmlFor="apply_sale_price_to_all">
												{t('apply_sale_price_to_all')}
												<HintIcon text={data.apply_sale_price_to_all
													? t('apply_sale_price_to_all_hint', { selected: data.sale_price_overridden_pos_count })
													: t('sale_price_pos_scope_hint', { selected: data.sale_price_overridden_pos_count })} />
											</label>
										</div>
									</div>
								}
								<div className="flex-gap10">
									<div className="form-group w-100">
										<label>{t('wms')}<span className="required-mark">*</span></label>
										<Select
											options={wms}
											value={wms.find(option => option.id === data.wms_id)}
											onChange={(option) => setData({ ...data, 'wms_id': option.id })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
									<div className="form-group w-100">
										<label>{t('unit')}<span className="required-mark">*</span></label>
										<Select
											options={units}
											value={units.find(option => option.id === data.unit_id)}
											onChange={(option) => setData({ ...data, 'unit_id': option.id })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
									<div className="form-group w-100">
										<label>{t('weight')}</label>
										<input type="number" className="form-control"
											name="weight"
											value={data.weight ?? ''}
											onChange={(e) => setData({ ...data, 'weight': e.target.value })} />
									</div>
								</div>
								<div className="flex-gap10">
									<div className="form-group w-100">
										<label>{t("write_off_method")}<span className="required-mark">*</span></label>
										<Select
											options={writeOffTypes}
											value={writeOffTypes.find(option => option.id === data.write_off_type_id)}
											onChange={(option) => setData({ ...data, 'write_off_type_id': option.id })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
									<div className="form-group w-100">
										<label>{t("time")}</label>
										<MaskedInput
											mask={[/[0-9]/, /\d/, ':', /\d/, /\d/, ':', /\d/, /\d/,]}
											className="form-control"
											placeholder="00:00:00"
											guide={true}
											ref={inputRef}
											value={data.cooking_time ?? ''}
										/>
									</div>
								</div>
							</div>
							<div className="col-md-7">
								<div className={activeTab !== 5 ? 'd-none' : ''}>
									<div className="input-borer-only p-3 mb-3">
										<div className="flex-gap10">
											<div className="form-group w-100">
												<label>{t('product_categories')}</label>
												<Select
													components={{ GroupHeading: GroupHead, Option }}
													isClearable
													isSearchable={false}
													onMenuOpen={() => setIsOpen(true)}
													onMenuClose={() => setIsOpen(false)}
													onBlur={() => setIsOpen(false)}
													menuIsOpen={isOpen}
													classNamePrefix='react-select'
													options={categories}
													value={categoriesInitial.find(option => option.id === data.category_id)}
													onChange={(option) => selectCategory(option)}
													placeholder={t('categories')}
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group w-100">
												<label>{t('accounting_categories')}</label>
												<Select
													isDisabled
													isClearable
													options={productCategories}
													value={productCategories.find(option => option.id === data.accounting_category_id)}
													onChange={(option) => setData({ ...data, 'accounting_category_id': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>

										<div className="form-group w-100">
											<label>
												{t("kitchen_name")}
												<HintIcon text={t('kitchen_name_hint')} />
											</label>
											<input type="text" className="form-control" maxLength={255}
												value={data.kitchen_name ?? ''}
												onChange={(e) => setData({ ...data, 'kitchen_name': e.target.value })} />
										</div>

										<div className="form-group w-100">
											<label>{t("description")}</label>
											<textarea
												className="form-control"
												name="description"
												maxLength={500}
												rows={3}
												value={data.description ?? ''}
												onChange={(e) => setData({ ...data, description: e.target.value })}
											/>
											<div className="d-flex justify-content-end mt-1">
												<small className="text-muted">
													{(data.description ?? '').length}/500
												</small>
											</div>
										</div>
										<hr />
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center" title={t('on_sale_hint')}><span>{t('on_sale')}<HintIcon text={t('on_sale_hint')} /></span></p>
											<div className="form-check form-switch form-switch-lg">
												<input type="checkbox" className="form-check-input" title={t('on_sale_hint')} name="status"
													checked={!!data.status}
													onChange={(e) => setData({ ...data, 'status': e.target.checked })}
												/>
											</div>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center" title={t('devisible_product_hint')}><span>{t('devisible_product')}<HintIcon text={t('devisible_product_hint')} /></span></p>
											<div className="form-check form-switch form-switch-lg">
												<input type="checkbox" className="form-check-input" title={t('devisible_product_hint')} name="is_divisible"
													checked={!!data.is_divisible}
													onChange={(e) => setData({
														...data,
														'is_divisible': e.target.checked,
													})}
												/>
											</div>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center" title={t('custom_price_hint')}><span>{t('custom_price')}<HintIcon text={t('custom_price_hint')} /></span></p>
											<div className="form-check form-switch form-switch-lg">
												<input type="checkbox" className="form-check-input" title={t('custom_price_hint')} name="is_custom_price"
													checked={!!data.is_custom_price}
													onChange={(e) => setData({ ...data, 'is_custom_price': e.target.checked })}
												/>
											</div>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center" title={t('external_service_hint')}><span>{t('external_service')}<HintIcon text={t('external_service_hint')} /></span></p>
											<div className="form-check form-switch form-switch-lg">
												<input type="checkbox" className="form-check-input" title={t('external_service_hint')} name="is_external_menu_enabled"
													checked={!!data.is_external_menu_enabled}
													onChange={(e) => setData({
														...data,
														'is_external_menu_enabled': e.target.checked,
													})}
												/>
											</div>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center" title={t('print_ingredients_kitchen_hint')}><span>{t('print_ingredients_kitchen')}<HintIcon text={t('print_ingredients_kitchen_hint')} /></span></p>
											<div className="form-check form-switch form-switch-lg">
												<input type="checkbox" className="form-check-input" title={t('print_ingredients_kitchen_hint')} name="is_print_ingredients"
													checked={!!data.is_print_ingredients}
													onChange={(e) => setData({ ...data, 'is_print_ingredients': e.target.checked })}
												/>
											</div>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center" title={t('is_service_charge_enabled_hint')}><span>{t('is_service_charge_enabled')}<HintIcon text={t('is_service_charge_enabled_hint')} /></span></p>
											<div className="form-check form-switch form-switch-lg">
												<input type="checkbox" className="form-check-input" title={t('is_service_charge_enabled_hint')} name="is_service_charge_enabled"
													checked={!!data.is_service_charge_enabled}
													onChange={(e) => setData({ ...data, 'is_service_charge_enabled': e.target.checked })}
												/>
											</div>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center" title={t('is_standalone_hint')}><span>{t('is_standalone')}<HintIcon text={t('is_standalone_hint')} /></span></p>
											<div className="form-check form-switch form-switch-lg">
												<input type="checkbox" className="form-check-input" title={t('is_standalone_hint')} name="is_standalone"
													checked={!!data.is_standalone}
													onChange={(e) => setData({ ...data, 'is_standalone': e.target.checked })}
												/>
											</div>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center" title={t('is_constructor_hint')}><span>{t('is_constructor')}<HintIcon text={t('is_constructor_hint')} /></span></p>
											<div className="form-check form-switch form-switch-lg">
												<input type="checkbox" className="form-check-input" title={t('is_constructor_hint')} name="is_constructor"
													checked={!!data.is_constructor}
													onChange={(e) => setData({
														...data,
														'is_constructor': e.target.checked,
														'constructor_type': e.target.checked ? (data.constructor_type || 'groups') : null,
														'constructor_view': e.target.checked ? (data.constructor_view || 'list') : null,
													})}
												/>
											</div>
										</div>
										{!!data.is_constructor &&
											<>
											<div className="form-group mb-2">
												<label>{t('constructor_type')}</label>
												<select className="form-select" name="constructor_type"
													value={data.constructor_type || 'groups'}
													onChange={(e) => setData({ ...data, 'constructor_type': e.target.value })}>
													<option value="groups">{t('constructor_type_groups')}</option>
													<option value="combo">{t('constructor_type_combo')}</option>
													<option value="halves">{t('constructor_type_halves')}</option>
												</select>
												{data.constructor_type === 'combo' &&
													<div className="fz12 text-muted mt-1">{t('constructor_combo_hint')}</div>
												}
											</div>
											{data.constructor_type !== 'halves' &&
											<div className="form-group mb-2">
												<label>{t('constructor_view')}<HintIcon text={t('constructor_view_hint')} /></label>
												<select className="form-select" name="constructor_view"
													value={data.constructor_view || 'list'}
													onChange={(e) => setData({ ...data, 'constructor_view': e.target.value })}>
													<option value="list">{t('constructor_view_list')}</option>
													<option value="steps">{t('constructor_view_steps')}</option>
												</select>
											</div>
											}
											</>
										}
										<hr />
										<h6>{t('energy_value_per')}</h6>
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('proteins')}</label>
												<input type="number" className="form-control" name="proteins"
													value={data.proteins ?? ''}
													onChange={(e) => setData({ ...data, 'proteins': e.target.value })} />
											</div>
											<div className="form-group w-100">
												<label>{t('fat')}</label>
												<input type="number" className="form-control" name="fat"
													value={data.fat ?? ''}
													onChange={(e) => setData({ ...data, 'fat': e.target.value })} />
											</div>
											<div className="form-group w-100">
												<label>{t('carbohydrates')}</label>
												<input type="number" className="form-control" name="carbohydrates"
													value={data.carbohydrates ?? ''}
													onChange={(e) => setData({ ...data, 'carbohydrates': e.target.value })} />
											</div>
											<div className="form-group w-100">
												<label>{t('calories')}</label>
												<input type="number" className="form-control" name="calories"
													value={data.calories ?? ''}
													onChange={(e) => setData({ ...data, 'calories': e.target.value })} />
											</div>
										</div>
									</div>
								</div>
								<div className={activeTab !== 1 ? 'd-none' : ''}>
									<div className="input-borer-only p-3 mb-3">
										<div className="d-flex justify-content-between mb-2">
											<h4 className="me-2">{t('sizes')}</h4>
											{!hasNoVariants &&
												<div className="table-action-button table-action-success-button"
													onClick={() => addSizes()}>
													<i className="uil-plus"></i>
												</div>
											}
										</div>
										<Size
											posId={posId}
											data={data}
											setData={setData}
											activeTab={activeTab}>
										</Size>
									</div>
								</div>
								<div className={activeTab !== 2 ? 'd-none' : ''}>
									<div className="input-borer-only p-3 mb-3" style={{ 'minHeight': '240px' }}>
										<div className="d-flex justify-content-between mb-2">
											<div className="vertical-center">
												<h4>{t("modifiers")}</h4>
											</div>
											{!hasNoVariants &&
												<div className="d-flex gap-2">
													<button type="button" className="btn btn-primary btn-rounded btn-wide"
														onClick={() => setShowAddModifierModal(true)}>
														{t('add')}
													</button>
													<button type="button" className="btn btn-primary btn-rounded btn-wide"
														onClick={() => setShowModifierModal(true)}>
														{t('create')}
													</button>
												</div>
											}
										</div>

										<div className="table-responsive">
											<table className="table table-bordered">
												<thead>
													<tr>
														<th style={{ 'width': '22%' }}>{t('name2')}</th>
														<th style={{ 'width': '5%' }} className="text-end">Мин</th>
														<th style={{ 'width': '5%' }} className="text-end">Макс</th>
														<th style={{ 'width': '14%' }} className="text-end text-nowrap"
											title="Сколько штук варианта подставить сразу при добавлении блюда: 1 — вариант просто выбран заранее (кола в комбо), 2 — сразу две порции (гарнир)">По умолчанию</th>
														<th style={{ 'width': '11%' }} className="text-end text-nowrap"
											title="Сколько штук из группы бесплатно на одну порцию блюда («любые 2 соуса бесплатно»). Бесплатными считаются самые дорогие штуки, платят за самые дешёвые. 0 — платят за все">Бесплатно</th>
														<th style={{ 'width': '10%' }} className="text-center text-nowrap"
											title="Штуки по умолчанию входят в цену блюда, платно только то, что гость добавил сверх. Выключено — компонент всегда добавляется сверху">В цене</th>
														<th style={{ 'width': '15%', 'minWidth': '110px' }} className="text-end" title={t('modifier_price_hint')}>{t('modifier_price')}</th>
														<th style={{ 'width': '15%' }} className="text-center">Обязательный</th>
														{(!!data.is_constructor && data.constructor_type === 'halves') &&
															<th style={{ 'width': '12%' }} className="text-center text-nowrap"
																title="Пицца-половинки: включено — группа выбирается для каждой половинки отдельно (начинка), цена вариантов на половинку делится пополам. Выключено — общая для всей пиццы (размер, основа, соус)">По половинкам</th>
														}
														<th style={{ 'minWidth': '33px' }}></th>
													</tr>
												</thead>
												<tbody>
													{data?.modifiers?.map((modifier, index) => (
														<Fragment key={index}>
															<tr>
																<td>
																	<b>{modifier.name}</b>
																</td>
																<td className="td-input">
																	<input
																		type="number"
																		min="0"
																		step="1"
																		name="min"
																		className="form-control text-end"
																		placeholder="0"
																		value={modifier.min ?? ''}
																		onChange={(e) => handleChangeModifier(e, index)}
																	/>
																</td>
																<td className="td-input">
																	<input
																		type="number"
																		min="0"
																		step="1"
																		name="max"
																		className="form-control text-end"
																		placeholder="0"
																		value={modifier.max ?? ''}
																		onChange={(e) => handleChangeModifier(e, index)}
																	/>
																</td>
																<td className="td-input">
																	<input
																		disabled
																		type="number"
																		name="default_quantity"
																		className="form-control text-end"
																		placeholder="0"
																		value={modifier.default_quantity ?? ''}
																	/>
																</td>
																<td className="td-input">
																	<input
																		type="number"
																		min="0"
																		step="1"
																		name="free_quantity"
																		className="form-control text-end"
																		placeholder="0"
																		value={modifier.free_quantity ?? ''}
																		onChange={(e) => handleChangeModifier(e, index)}
																	/>
																</td>
																<td></td>
																<td></td>
																<td className="text-center">
																	<input
																		type="checkbox"
																		name="required"
																		className="form-check-input"
																		checked={modifier.required}
																		onChange={(e) => handleChangeModifier(e, index)}
																	/>
																</td>
																{(!!data.is_constructor && data.constructor_type === 'halves') &&
																	<td className="text-center">
																		<input
																			type="checkbox"
																			name="is_halvable"
																			className="form-check-input"
																			checked={!!modifier.is_halvable}
																			onChange={(e) => handleChangeModifier(e, index)}
																		/>
																	</td>
																}
																<td className="text-end">
																	<button type="button" className="table-action-button table-action-danger-button"
																		onClick={() => deleteModifier(modifier)}>
																		<i className="uil uil-times"></i>
																	</button>
																</td>
															</tr>
															{modifier?.products?.map((product, productIndex) => (
																<tr key={product.id ?? productIndex}>
																	<td className="td-ellipsis">
																		<div className="ms-2">- {product.name}</div>
																	</td>
																	<td className="td-input">
																		<input
																			type="number"
																			min="0"
																			step="1"
																			name="min"
																			className="form-control text-end"
																			placeholder="0"
																			value={product.min ?? ''}
																			onChange={(e) => handleChangeModifierProduct(e, index, productIndex)}
																		/>
																	</td>
																	<td className="td-input">
																		<input
																			type="number"
																			min="0"
																			step="1"
																			name="max"
																			className="form-control text-end"
																			placeholder="0"
																			value={product.max ?? ''}
																			onChange={(e) => handleChangeModifierProduct(e, index, productIndex)}
																		/>
																	</td>
																	<td className="td-input">
																		<input
																			type="number"
																			min="0"
																			step="1"
																			name="default_quantity"
																			className="form-control text-end"
																			placeholder="0"
																			value={product.default_quantity ?? ''}
																			onChange={(e) => handleChangeModifierProduct(e, index, productIndex)}
																		/>
																	</td>
																	<td></td>
																	<td className="text-center">
																		<input
																			type="checkbox"
																			name="base_price_included"
																			className="form-check-input"
																			checked={!!product.base_price_included}
																			onChange={(e) => handleChangeModifierProduct(e, index, productIndex)}
																		/>
																	</td>
																	<td className="td-input">
																		<input
																			type="number"
																			step="any"
																			name="delta_price"
																			className="form-control text-end"
																			placeholder={product.sale_price ?? ''}
																			title={t('modifier_price_hint')}
																			value={product.delta_price ?? ''}
																			onChange={(e) => handleChangeModifierProduct(e, index, productIndex)}
																		/>
																	</td>
																	<td></td>
																	<td></td>
																	{(!!data.is_constructor && data.constructor_type === 'halves') && <td></td>}
																</tr>
															))}
														</Fragment>
													))}
												</tbody>
											</table>
										</div>
									</div>

									<Modifiers
										showModifierModal={showModifierModal}
										setShowModifierModal={setShowModifierModal}
										showAddModifierModal={showAddModifierModal}
										setShowAddModifierModal={setShowAddModifierModal}
										addModifierToProduct={addModifierToProduct}
										attachedIds={(data?.modifiers || []).map((modifier) => modifier.product_modifier_id)}
										activeTab={activeTab}>
									</Modifiers>
								</div>
								<div className={activeTab !== 3 ? 'd-none' : ''}>
									<div className="input-borer-only p-3 mb-3">
										<div className="d-flex justify-content-between mb-2">
											<h4>{t('used_in_products')}</h4>
										</div>
										<div className="table-responsive">
											<table className="table table-bordered">
												<thead>
													<tr>
														<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
														<th style={{ 'width': '30%' }}>{t("product")}</th>
														<th style={{ 'width': '60%' }}></th>
														<th style={{ 'width': '10%' }} className="text-end">{t("quantity")}</th>
													</tr>
												</thead>
												<tbody>
													{data?.productInProducts?.length > 0 &&
														<>
															{data?.productInProducts?.map((item, index) => (
																<tr key={index}>
																	<td className="text-center">{index + 1}</td>
																	<td>
																		<Link to={`/products/update/${posId}/${item.product_id}`} target="_blank" rel="noopener noreferrer">
																			{item.product_name}
																		</Link>
																	</td>
																	<td></td>
																	<td className="text-end">{item.quantity}</td>
																</tr>
															))}
														</>
													}
												</tbody>
											</table>
										</div>
									</div>
								</div>
								<div className={activeTab !== 4 ? 'd-none' : ''}>
									{/* Метки на карточке блюда у гостя: сайт, Telegram-бот, приложение */}
									<div className="input-borer-only p-3 mb-3">
										<div className="d-flex justify-content-between align-items-center mb-2">
											<h6 className="mb-0">
												{t('product_labels')}
												<HintIcon text={t('product_labels_hint')} />
												<small className={`ms-2 fw-normal ${(data.labels ?? []).length > PRODUCT_LABELS_MAX ? 'text-danger' : 'text-muted'}`}
													title={t('product_labels_max_hint', { max: PRODUCT_LABELS_MAX })}>
													{t('product_labels_selected', { count: (data.labels ?? []).length, max: PRODUCT_LABELS_MAX })}
												</small>
											</h6>
											<a href="/product-labels" target="_blank" rel="noreferrer" className="fz14">
												{t('product_labels_manage')}
											</a>
										</div>
										{productLabels.length === 0 &&
											<div className="text-muted">{t('list_empty')}</div>
										}
										{productLabels.map((label) => {
											const link = (data.labels ?? []).find((item) => item.product_label_id === label.id)
											const expired = !!link?.ends_at && link.ends_at < new Date().toISOString().slice(0, 10)
											const blocked = !link && (data.labels ?? []).length >= PRODUCT_LABELS_MAX
											return (
												<div key={label.id} className="d-flex align-items-center justify-content-between gap-2 py-1">
													<div className="d-flex align-items-center gap-2">
														<input type="checkbox" className="form-check-input mt-0" id={`label_${label.id}`}
															checked={!!link}
															disabled={blocked}
															title={blocked ? t('product_labels_max_hint', { max: PRODUCT_LABELS_MAX }) : ''}
															onChange={() => toggleProductLabel(label.id)} />
														<label htmlFor={`label_${label.id}`} className="mb-0"
															title={blocked ? t('product_labels_max_hint', { max: PRODUCT_LABELS_MAX }) : ''}
															style={{ cursor: blocked ? 'not-allowed' : 'pointer', opacity: link ? 1 : (blocked ? 0.3 : 0.55) }}>
															<ProductLabelBadge label={label} />
														</label>
														{expired && <small className="text-danger">{t('label_expired')}</small>}
													</div>
													{link &&
														<div className="d-flex align-items-center gap-2">
															<small className="text-muted">{t('until')}</small>
															<input type="date" className="form-control form-control-sm" style={{ width: '150px' }}
																title={t('label_ends_at_hint')}
																value={link.ends_at ?? ''}
																onChange={(e) => setProductLabelEndsAt(label.id, e.target.value)} />
														</div>
													}
												</div>
											)
										})}
									</div>
									<div className="input-borer-only p-3 mb-3 d-none">
										<h4 className="fw-600">{t('tg_bot')}</h4>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center">{t('text')}</p>
											<div className="form-group">
												<input type="text" className="form-control"
													name="tg_text"
													maxLength={4}
													placeholder={t('text')}
													value={data?.params?.tg_text ?? ''}
													onChange={(e) => handleParamsChange('tg_text', e.target.value)} />
											</div>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center">{t('color')}</p>
											<div className="form-group">
												<div className="position-relative">
													<input type="text" className="form-control"
														placeholder={t('color')}
														value={data?.params?.tg_color ?? ''}
														onChange={(e) => handleParamsChange('tg_color', e.target.value)} />
													<i className="color-picker-icon uil-angle-down"
														onClick={() => setData({ ...data, 'bool_color_picker': !data.bool_color_picker })}>
													</i>
												</div>
												{data.bool_color_picker &&
													<SketchPicker
														style={{ 'zIndex': 1 }}
														className="position-absolute"
														onChangeComplete={(option) => handleParamsChange('tg_color', option.hex)}
													/>
												}
											</div>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600 vertical-center">{t('bg_color')}</p>
											<div className="form-group">
												<div className="position-relative">
													<input type="text" className="form-control"
														placeholder={t('bg_color')}
														value={data?.params?.tg_bg_color ?? ''}
														onChange={(e) => handleParamsChange('tg_bg_color', e.target.value)} />
													<i className="color-picker-icon uil-angle-down"
														onClick={() => setData({ ...data, 'bool_color_picker2': !data.bool_color_picker2 })}>
													</i>
												</div>
												{data.bool_color_picker2 &&
													<SketchPicker
														style={{ 'zIndex': 1 }}
														className="position-absolute"
														onChangeComplete={(option) => handleParamsChange('tg_bg_color', option.hex)}
													/>
												}
											</div>
										</div>
									</div>
								</div>

							</div>
						</div>

						<div className={mainActiveTab === 2 ? '' : 'd-none'}>
							<div className="table-responsive">
								<table className="table table-bordered">
									<thead>
										<tr>
											<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
											<th style={{ 'width': '30%' }}>
												<div className="d-flex justify-content-between align-items-center">
													<span>{t("name2")}</span>
													<div className="d-flex gap-1">
														<div className="table-action-button table-action-primary-button"
															title={t('recipe_import')}
															onClick={() => toggleModalImportRecipe(true)}>
															<i className="uil-import"></i>
														</div>
														<div className="table-action-button table-action-primary-button"
															onClick={() => toggleModalElement(true)}>
															<i className="uil-plus"></i>
														</div>
													</div>
												</div>
											</th>
											<th style={{ 'width': '10%' }}>{t("type")}</th>
											<th style={{ 'width': '5%' }} className="text-nowrap">{t("unit")}</th>
											<th style={{ 'width': '10%' }} className="text-end">{t("quantity")}</th>
											<th style={{ 'width': '10%' }}>Брутто, {t('kg')}</th>
											<th style={{ 'width': '10%' }}>{t('cold_processing_loss')} %</th>
											<th style={{ 'width': '10%' }}>Нетто, {t('kg')}</th>
											<th style={{ 'width': '10%' }}>{t('hot_processing_loss')} %</th>
											<th style={{ 'width': '10%' }}>{t('finished_product')} {t('kg')}</th>
											<th style={{ 'width': '10%' }} className="text-nowrap text-end">{t("cost_price")}</th>
											<th style={{ 'width': '10%' }} className="text-nowrap text-end">1-{t("cost_price")}</th>
											<th style={{ 'minWidth': '64px' }} className="text-center">
												{data?.ingredients?.length > 0 &&
													<div
														className="table-action-button table-action-danger-button mx-auto"
														title={t('delete_all')}
														onClick={() => setModalDeleteAllIngredients(true)}>
														<i className="uil-times"></i>
													</div>
												}
											</th>
										</tr>
									</thead>
									<tbody>
										{data?.ingredients?.map((item, index) => (
											<tr key={index}>
												<td className="text-center" title={item?.id ?? null}>
													{item.id &&
														<Link to={`/products/update/${posId}/${item.element_id}`} target="_blank" rel="noopener noreferrer">
															{index + 1}
														</Link>
													}
													{!item.id &&
														<span>{index + 1}</span>
													}
												</td>
												<td className="td-ellipsis">
													{(id && item.id) ?
														<div title={formatDate(item.created_at, 'dd.MM.yy HH:mm')}>{item.name}</div>
														:
														<div>
															<ReactSelect
																uniqueId="ingredient"
																menuMinWidth={620}
																search={handleSelectSearch}
																value={item.name}
																index={index}
																scrollRef={scrollRef}
																options={elements}
																onClose={() => closeReactSelect()}
																selectItem={(option) => handleSelectElement(option, index)}
																onKeyUp={(e) => {
																	if (e.keyCode === 40 || e.keyCode === 38) {
																		handleTableKeyDown(e)
																	}
																}}
																table={
																	<table className="table mb-0">
																		<tbody ref={tbodyRef}>
																			{elements.map((selectItem, selectIndex) => (
																				<tr
																					id={selectItem.id}
																					className={`cursor ${((selectItem.selected || selectItem.id === item.element_id) && 'selected')}`}
																					tabIndex={-1}
																					key={selectIndex}
																					onClick={() => handleSelectElement(selectItem, index)}
																					onKeyDown={(e) => handleTableKeyDown(e, selectItem, index)}>
																					<td>{selectItem.name}</td>
																					<td className="text-nowrap">{selectItem.product_type_name}</td>
																					<td className="text-end text-nowrap">{formatMoney(
																						selectItem.second_unit_id && Number(selectItem.product_type_id ?? selectItem.in_product) !== 2
																							? selectItem.average_price
																							: (Number(selectItem.average_price) || 0) / (Number(selectItem.final_quantity) > 0 ? Number(selectItem.final_quantity) : 1)
																					)}</td>
																				</tr>
																			))}
																		</tbody>
																	</table>
																}
															/>
														</div>
													}
												</td>
												<td>{item.product_type_name}</td>
												<td>{item.second_unit_id ? item.second_unit_name : item.unit_name}</td>
												<td className="td-input" style={!item.quantity ? { 'borderBottom': '2px solid red' } : {}}>
													<input type="number" className="form-control text-end" name="quantity"
														disabled={item.id}
														placeholder="0"
														value={!item.id ? (item.quantity ?? '') : formatMoney(item.quantity, 4)}
														onChange={(e) => handleIngridientChange(e, index)}
														onKeyUp={(e) => {
															if (e.keyCode === 13) {
																calculateWeight(index)
															}
														}} />
												</td>
												<td className="text-end">{formatMoney(item.weight_gross, 4)}</td>
												<td className="td-input">
													<input type="number" className="form-control text-end" name="loss_cold"
														placeholder="0"
														value={item.loss_cold ?? ''}
														onChange={(e) => handleIngridientChange(e, index)}
														onKeyUp={(e) => {
															if (e.keyCode === 13) {
																calculateWeight(index)
															}
														}} />
												</td>
												<td className="td-input">
													<input type="number" className="form-control text-end" name="weight_net"
														disabled={item.id}
														placeholder="0"
														value={!item.id ? (item.weight_net ?? '') : formatMoney(item.weight_net, 4)}
														onChange={(e) => handleIngridientChange(e, index)}
														onKeyUp={(e) => {
															if (e.keyCode === 13) {
																calculateWeight(index)
															}
														}} />
												</td>
												<td className="td-input">
													<input type="number" className="form-control text-end" name="loss_hot"
														placeholder="0"
														value={item.loss_hot ?? ''}
														onChange={(e) => handleIngridientChange(e, index)}
														onKeyUp={(e) => {
															if (e.keyCode === 13) {
																calculateWeight(index)
															}
														}} />
												</td>
												<td className="td-input">
													<input type="number" className="form-control text-end" name="weight_finished"
														placeholder="0"
														value={!item.id ? (item.weight_finished ?? '') : formatMoney(item.weight_finished, 4)}
														onChange={(e) => handleIngridientChange(e, index)}
														onKeyUp={(e) => {
															if (e.keyCode === 13) {
																calculateWeight(index)
															}
														}} />
												</td>
												<td className="text-end">
													{item.cost_price ? formatMoney(item.cost_price, 2) : 0}
												</td>
												<td className="text-nowrap text-end" >
													{item.average_price ? formatMoney(getIngredientUnitPrice(item)) : 0}
												</td>
												<td className="vertical-middle">
													<div className="d-flex justify-content-center gap-1">
														{data.product_type_id !== 2 &&
															<div className="table-action-button table-action-primary-button"
																onClick={() => toggleIngredientParamsModal(true, index, item)}>
																<i className="uil-setting"></i>
															</div>
														}
														<div className="table-action-button table-action-danger-button"
															onClick={() => toggleModalDeleteIngredient(item, index)}>
															<i className="uil-times"></i>
														</div>
													</div>
												</td>
											</tr>
										))}
									</tbody>
									<tfoot>
										<tr>
											<td></td>
											<td colSpan={1} style={{ 'padding': 0 }}>
												<div className="d-flex w-100">
													<button type="button" className="btn-sm btn-primary w-100"
														onClick={() => addIngredients()}>
														{t('add')}
													</button>
												</div>
											</td>
											<td style={{ 'padding': 0 }}>
												<div className="d-flex w-100">
													<button type="button" className="btn-sm btn-primary w-100"
														onClick={() => {
															if (calculateDirection === 'right') {
																setCalculateDirection('left')
															} else {
																setCalculateDirection('right')
															}
														}}>
														{calculateDirection === 'right' &&
															<i className="uil-arrow-right"></i>
														}
														{calculateDirection === 'left' &&
															<i className="uil-arrow-left"></i>
														}
													</button>
												</div>
											</td>
											<td></td>
											<td></td>
											<td className="text-nowrap text-end"><b>{formatMoney(data.overall_weight_gross, 4)}</b></td>
											<td></td>
											<td className="text-nowrap text-end"><b>{formatMoney(data.overall_weight_net, 4)}</b></td>
											<td></td>
											<td className="text-nowrap text-end"><b>{formatMoney(data.overall_weight_finished, 4)}</b></td>
											<td className="text-nowrap text-end"><b>{formatMoney(data.cost_price, 2)}</b></td>
											<td></td>
											<td colSpan={1} style={{ 'padding': 0 }}>
												{id &&
													<div className="d-flex w-100">
														<button type="button" className="btn-sm btn-primary w-100"
															onClick={() => toggleModalIngredientsHistory(true)}>
															<i className="uil-list-ui-alt"></i>
														</button>
													</div>
												}
											</td>
										</tr>
									</tfoot>
								</table>
								<div className="d-flex gap-2" style={{ 'width': '400px' }}>
									<div className="form-group w-100">
										<label>
											{t('prep_output_quantity')}
											{' '}({findKeyFromArrayByValue(units, 'id', data.unit_id, 'name')})
										</label>
										<input type="number" className="form-control"
											disabled={data.product_type_id === 1 && !(data.ingredients || []).some((item) => item.element_id)}
											name="quantity"
											value={data.quantity ?? ''}
											onChange={(e) => setData({ ...data, 'quantity': e.target.value })} />
									</div>
									<div className="form-group w-100">
										<label>{t('weight')}</label>
										<input type="number" className="form-control"
											name="weight"
											value={data.weight ?? ''}
											onChange={(e) => setData({ ...data, 'weight': e.target.value })} />
									</div>
								</div>
								<div style={{ 'width': '400px' }}>
									<div className="d-flex justify-content-between">
										<p className="fw-600 vertical-center">{t('inventory_basaed_stop_list')}</p>
										<div className="form-check form-switch form-switch-lg">
											<input type="checkbox" className="form-check-input" name="is_stock_controlled"
												checked={!!data.is_stock_controlled}
												onChange={(e) => setData({ ...data, 'is_stock_controlled': e.target.checked })}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className={mainActiveTab === 3 ? '' : 'd-none'}>
							{/* одной колонкой: ИКПУ длинный — на всю ширину, короткие поля парами */}
							<div className="row">
								<div className="col-lg-6 col-md-8">
									<div className="form-group">
										<div className="d-flex justify-content-between">
											<label>ИКПУ</label>
											<div className="table-action-button table-action-success-button"
												onClick={() => toggleModalFiscalization(true)}>
												<i className="uil-plus"></i>
											</div>
										</div>
										<Select
											isClearable
											options={fiscalCodes}
											value={fiscalCodes.find(option => option.id === data.fiscal_code)}
											onChange={(option) => handleSelectFiscalCode(option)}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
									<div className="flex-gap10">
										<div className="form-group w-100">
											<label>{t("unit")}</label>
											<Select
												isClearable
												options={fiscalPackageCodes}
												value={fiscalPackageCodes.find(option => option.id === data.fiscal_package_code)}
												onChange={(option) => handleSelectFiscalPackageCode(option)}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
										<div className="form-group w-100">
											<label>{t('tax_categories')}</label>
											<Select
												isClearable
												options={taxCategories}
												value={taxCategories.find(option => option.id === data.tax_category_id)}
												onChange={(option) => setData({ ...data, 'tax_category_id': option ? option.id : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="flex-gap10">
										<div className="form-group w-100">
											<label>{t('barcode')}</label>
											<input type="number" className="form-control" name="barcode"
												value={data.barcode ?? ''}
												onChange={(e) => handleChange(e)} />
										</div>
										<div className="form-group w-100">
											<label>{t('code')}(PLU)</label>
											<input type="number" className="form-control" name="plu"
												value={data.plu ?? ''}
												onChange={(e) => handleChange(e)} />
										</div>
									</div>
									<div className="d-flex justify-content-between">
										<p className="fw-600 vertical-center" title={t('marking_hint')}><span>{t('marking')}<HintIcon text={t('marking_hint')} /></span></p>
										<div className="form-check form-switch form-switch-lg">
											<input type="checkbox" className="form-check-input" title={t('marking_hint')} name="is_marking"
												checked={!!data.is_marking}
												onChange={(e) => setData({ ...data, 'is_marking': e.target.checked })}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Продаётся на точках — прейскурант филиалов, а не свойство карточки */}
						<div className={mainActiveTab === 4 ? '' : 'd-none'}>
							<div className="row">
								<div className="col-lg-6 col-md-8">
									<div className="border rounded">
										{/* Шапка: общий чекбокс и поиск — как в диалоге выбора подразделений iiko */}
										<div className="d-flex align-items-center gap-3 border-bottom px-3 py-2">
											<label className="d-flex align-items-center gap-2 mb-0"
												style={{ cursor: 'pointer' }}>
												<input type="checkbox" className="form-check-input flex-shrink-0 mt-0"
													checked={allVisiblePosesSelected}
													ref={(el) => { if (el) el.indeterminate = someVisiblePosesSelected }}
													onChange={(e) => setPosesSale(e.target.checked)} />
												<span className="fw-600">{t('select_all')}</span>
											</label>

											{/* Поиск нужен сети, а не заведению с парой точек */}
											{posesSale.length > 8 &&
												<div className="flex-grow-1">
													<input type="text" className="form-control form-control-sm"
														placeholder={t('search')}
														value={posSaleSearch}
														onChange={(e) => setPosSaleSearch(e.target.value)} />
												</div>
											}

											<span className="text-muted text-nowrap ms-auto">
												{t('selected_of', { selected: posesSaleSelected, total: posesSale.length })}
												<HintIcon text={t('sold_at_poses_hint')} />
											</span>
										</div>

										<div style={{ maxHeight: '420px', overflowY: 'auto' }}>
											{filteredPosesSale.map((item, index) => (
												<label className={`d-flex align-items-center gap-2 px-3 py-2 mb-0 pos-sale-row
													${index === filteredPosesSale.length - 1 ? '' : 'border-bottom'}`}
													style={{ cursor: 'pointer' }}
													key={item.pos_id}>
													<input type="checkbox" className="form-check-input flex-shrink-0 mt-0"
														checked={!!item.is_on_sale}
														disabled={item.is_menu_allowed === false}
														onChange={(e) => togglePosSale(item.pos_id, e.target.checked)} />
													<span className={`text-truncate ${item.is_menu_allowed === false ? 'text-muted' : ''}`}>
														{item.name}
													</span>

													{/* Раздел закрыт для точки — галочка бессильна, объясняем прямо в строке */}
													{item.is_menu_allowed === false &&
														<span className="text-muted text-nowrap ms-auto" title={t('hidden_by_menu_hint')}>
															{t('hidden_by_menu')}
														</span>
													}
												</label>
											))}

											{!filteredPosesSale.length &&
												<div className="text-muted text-center p-3">{t('list_empty')}</div>
											}
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="btn-wrapper gap-2">
							{embedded ?
								<button type="button" className="btn btn-warning btn-rounded btn-wide"
									onClick={() => onClose && onClose()}>
									{t('cancel')}
								</button>
								:
								<Link className="btn btn-warning btn-rounded btn-wide" to="/products"
									onClick={closeTabOrBack}>
									{t('cancel')}
								</Link>
							}
							{!data.id &&
								<button type="button" className="btn btn-secondary btn-rounded btn-wide"
									onClick={(e) => createUpdate(e, true)}>
									{t('save')}
									<i className="uil uil-arrow-right"></i>
								</button>
							}
							<button type="button" className="btn btn-primary btn-rounded btn-wide"
								onClick={(e) => createUpdate(e)}>
								{t('save')}
							</button>
						</div>
					</form>
				</div>
			</div>

			{/* MODAL INGREDIENT PARAMS */}
			<Modal show={showIngredientParamsModal.bool} animation={false} centered
				dialogClassName="update-modal-width" onHide={() => {
					toggleIngredientParamsModal()
				}}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('settings')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<span className="text-danger">{t('write_off_ingredient_only')}</span>
					<div className="d-flex justify-content-between mb-2">
						<p className="fw-600 vertical-center">{t('hall')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="hall"
								checked={showIngredientParamsModal?.params?.hall}
								onChange={(e) => handleIngredientParamsChange(e, 'checkbox')} />
						</div>
					</div>
					<div className="d-flex justify-content-between mb-2">
						<p className="fw-600 vertical-center">{t('takeaway')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="takeaway"
								checked={showIngredientParamsModal?.params?.takeaway}
								onChange={(e) => handleIngredientParamsChange(e, 'checkbox')} />
						</div>
					</div>
					<div className="d-flex justify-content-between mb-2">
						<p className="fw-600 vertical-center">{t('delivery')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="delivery"
								checked={showIngredientParamsModal?.params?.delivery}
								onChange={(e) => handleIngredientParamsChange(e, 'checkbox')} />
						</div>
					</div>
					<hr />
					<div className="d-flex justify-content-between mb-2">
						<p className="fw-600 vertical-center">{t('changeable')}</p>
						<div className="form-check form-switch form-switch-lg">
							<input type="checkbox" className="form-check-input" name="changeable"
								checked={showIngredientParamsModal?.changeable}
								onChange={(e) => handleIngredientParamsChange(e, 'checkbox')} />
						</div>
					</div>
					<hr />
					<div className="form-group w-100">
						<label>{t("write_off_method")}</label>
						<Select
							options={writeOffTypes}
							value={writeOffTypes.find(option => option.id === showIngredientParamsModal?.write_off_type_id)}
							onChange={(option) => setShowIngredientParamsModal({ ...showIngredientParamsModal, 'write_off_type_id': option.id })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>
					<input type="text" className="form-control" name="note"
						autoComplete="off"
						placeholder={t('note')}
						value={showIngredientParamsModal?.note ?? ''}
						onChange={(e) => handleIngredientParamsChange(e, 'text')} />
					<div className="d-flex gap-2 w-100 mt-3">
						<button type="button" className="btn btn-outline-warning w-100"
							onClick={() => toggleIngredientParamsModal()}>
							{t('cancel')}
						</button>
						<button type="button" className="btn btn-primary w-100"
							onClick={() => saveIngredientParams()}>
							{t('save')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL INGREDIENT PARAMS */}

			{/* MODAL INGREDIENT HISTORY */}
			<Modal show={modalIngredientsHistory} animation={false} centered size="lg"
				dialogClassName="update-modal-width"
				onHide={() => toggleModalIngredientsHistory()}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('history')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '80%' }}>{t('name2')}</th>
									<th style={{ 'width': '10%' }} className="text-end text-nowrap">{t('quantity')}</th>
									<th style={{ 'width': '10%' }} className="text-start text-nowrap">{t('unit')}</th>
									<th style={{ 'minWidth': '125px' }} className="text-center">{t('created')}</th>
									<th style={{ 'minWidth': '125px' }} className="text-center">{t('deleted')}</th>
									<th style={{ 'minWidth': '125px' }} className="text-center">{t('deleted_by')}</th>
								</tr>
							</thead>
							<tbody>
								{ingredientsHistory?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{index + 1}</td>
										<td className="td-ellipsis" title={item.name}>{item.name}</td>
										<td className="text-end">{item.quantity}</td>
										<td className="text-start">{item.unit_name}</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yyyy HH:mm')}</td>
										<td className="text-center">{formatDate(item.deleted_at, 'dd.MM.yyyy HH:mm')}</td>
										<td className="text-center" title={item.deleted_by}>{item.deleted_by}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL INGREDIENT HISTORY */}

			{/* MODAL IMPORT RECIPE */}
			<Modal show={modalImportRecipe.bool} animation={false} centered
				onHide={() => toggleModalImportRecipe(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('recipe_import')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form autoComplete="off" onSubmit={(e) => { e.preventDefault(); importRecipe() }}>
					<div className="form-group">
						<label>{t('recipe_import_source')}</label>
						<Select
							autoFocus
							options={modalImportRecipe.products}
							value={modalImportRecipe.product}
							onChange={(option) => setModalImportRecipe({ ...modalImportRecipe, 'product': option })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => `${option.name} (${option.product_type_name}, ${option.ingredients_count})`}
							getOptionValue={(option) => option.id}
							components={{ IndicatorSeparator: () => null }} />
					</div>
					<div className="text-muted small mt-2">
						{t('recipe_import_hint')}
					</div>
					<div className="d-flex gap-2 w-100 mt-3">
						<button type="button" className="btn btn-outline-warning w-100"
							onClick={() => toggleModalImportRecipe(false)}>
							{t('cancel')}
						</button>
						<button type="submit" className="btn btn-primary w-100"
							disabled={!modalImportRecipe.product}>
							{t('recipe_import_button')}
						</button>
					</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL IMPORT RECIPE */}

			{/* MODAL NEW FISCAL CODE */}
			<Modal Modal show={modalFiscal.bool} animation={false} centered
 onHide={() => toggleModalFiscalization(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('fiscalization')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="w-100">
						<div className="form-group">
							<label>ИКПУ [<a href="https://tasnif.soliq.uz/" target="_blank" rel="noreferrer">tasnif.soliq.uz</a>]</label>
							<input type="number" className="form-control"
								autoComplete="off"
								value={fiscalCode}
								onChange={(e) => setFiscalCode(e.target.value)} />
						</div>

						<button type="submit" className="btn btn-primary w-100 mt-2"
							onClick={() => createFiscalCode()}>
							{t('add')}
						</button>
					</div>
				</Modal.Body>
			</Modal >
			{/* MODAL NEW FISCAL CODE */}

			<QuickElementModal
				show={modalElement}
				onClose={() => toggleModalElement(false)}
				onCreated={handleCreatedElement}>
			</QuickElementModal>

			<DeleteModal
				modalDelete={modalDeleteIngredient}
				setModalDelete={setModalDeleteIngredient}
				deleteItem={deleteIngredient}>
			</DeleteModal>

			<DeleteModal
				modalDelete={modalDeleteAllIngredients}
				setModalDelete={setModalDeleteAllIngredients}
				deleteItem={deleteAllIngredients}
				message={t('are_you_sure_you_want_delete_all_ingredients')}>
			</DeleteModal>
		</>
	)
}

export default CreateUpdate